'use client'

import { DynamicPicture } from '@/components/atoms/DynamicPicture'
import { FlyerTemplate, Orientation } from '@/features/flyer/model/templates'
import forward_arrow_blue from '@public/images/forward_arrow_blue.png'
import forward_arrow_white from '@public/images/forward_arrow_white.png'
import { clsx } from 'clsx'
import Image from 'next/image'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import SwiperCore, { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'

interface SwiperWrapperBYFProps {
	images?: string[]
	imgPadding?: string
	selectedImage?: string
	onImageSelected: (src: string) => void
	onDragStart?: (e: React.DragEvent<HTMLImageElement>, src: string) => void
	orientation?: Orientation
	templates?: FlyerTemplate[]
	basePath?: 'websphere-base' | 'websphere-images' | 'included' | undefined
	imageProps?: Record<string, number>
	blockTouchToSwipe?: boolean
}

const SwiperWrapperBYF = (props: SwiperWrapperBYFProps) => {
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)
	const swiperRef = useRef<SwiperCore | null>(null)

	const handlePrevSlide = () => {
		if (swiperRef.current) {
			swiperRef.current.slidePrev()
			setIsBeginning(swiperRef.current.isBeginning)
			setIsEnd(swiperRef.current.isEnd)
		}
	}
	const handleNextSlide = () => {
		if (swiperRef.current) {
			swiperRef.current.slideNext()
			setIsBeginning(swiperRef.current.isBeginning)
			setIsEnd(swiperRef.current.isEnd)
		}
	}

	const onSwiperInit = (swiper: SwiperType) => {
		swiperRef.current = swiper
		setIsBeginning(swiper.isBeginning)
		setIsEnd(swiper.isEnd)
	}

	const onSlideChange = () => {
		if (swiperRef.current) {
			setIsBeginning(swiperRef.current.isBeginning)
			setIsEnd(swiperRef.current.isEnd)
		}
	}
	// forcefully update swiper when image container changes
	useEffect(() => {
		if (swiperRef.current) {
			setTimeout(() => {
				swiperRef.current?.update()
				swiperRef.current?.slideTo(0)
				setIsBeginning(swiperRef.current!.isBeginning)
				setIsEnd(swiperRef.current!.isEnd)
			}, 0)
		}
	}, [props.images?.length || props.templates?.length])

	const slidesPerView = useMemo(() => {
		const length = props.images?.length || props.templates?.length || 0
		return length > 2 && !props.templates ? 3 : 'auto'
	}, [props.images?.length || props.templates?.length])

	const imgShadeCover = <div className="absolute inset-0 z-10 rounded border-4 border-black inset-0 bg-gradient-to-b from-transparent via-[rgba(0,0,0,0.197621)] to-[#000033] pointer-events-none" style={{ width: '115px', height: '77px' }}></div>
	const imgShadeCoverWithImageProps = <div className="absolute inset-0 z-10 rounded border-4 border-black inset-0 bg-gradient-to-b  pointer-events-none" style={{ width: `${props.imageProps?.width}px`, height: `${props.imageProps?.height}px` }}></div>

	if ((!props.images || props.images.length === 0) && (!props.templates || props.templates.length === 0)) {
		return null
	}

	return (
		<>
			<Swiper onInit={onSwiperInit} onSlideChange={onSlideChange} slidesPerView={slidesPerView} spaceBetween={!props.templates ? 8 : 56} navigation={(props.images && props.images.length > 3) || (props.templates && props.templates.length > 3) ? { prevEl: '.custom-prev', nextEl: '.custom-next' } : false} watchOverflow={true} allowTouchMove={!props.blockTouchToSwipe} simulateTouch={!props.blockTouchToSwipe} className={`flex justify-center items-center max-w-full ${props.orientation ? 'mx-4 justify-self-center' : ''}`}>
				{props.images &&
					props.images.map((src: string, index: number) => (
						<>
							{!props.orientation && (
								<SwiperSlide key={index} className="relative w-fit">
									<DynamicPicture
										basepath={props.basePath ? props.basePath : undefined}
										style={{ width: props.imageProps ? `${props.imageProps.width}px` : '115px', height: props.imageProps ? `${props.imageProps.height}px` : '77px', padding: props.imgPadding }}
										width={props.imageProps ? props.imageProps.width : 115}
										height={props.imageProps ? props.imageProps.height : 77}
										image={src}
										alt=""
										className="cursor-pointer border-2 rounded"
										draggable="true"
										onClick={() => props.onImageSelected(src)}
										onDragStart={(e) => {
											props.onDragStart?.(e, src)
											e.stopPropagation()
										}}
									/>
									{props.selectedImage === src && !props.imageProps && imgShadeCover}
									{props.selectedImage === src && props.imageProps && imgShadeCoverWithImageProps}
								</SwiperSlide>
							)}
						</>
					))}

				{props.templates &&
					props.templates.map((flyerTemplate: FlyerTemplate, index: number) => {
						return (
							props.orientation && (
								<SwiperSlide
									key={index}
									className={clsx('relative w-fit h-fit transition-scale', {
										'cursor-pointer': flyerTemplate.isSelectable,
									})}
								>
									<div className="relative w-fit h-fit">
										<div
											className={`flex flex-col bg-background gap-2.5 p-5 overflow-hidden ${props.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet aspect-w-9 aspect-h-16' : 'w-printing-landscape-sheet h-printing-landscape-sheet aspect-w-16 aspect-h-9'} bg-white ${props.selectedImage === flyerTemplate.identifier ? 'rounded border-8 border-black pointer-events-none' : ''}`}
											onClick={() => {
												if (flyerTemplate.isSelectable) props.onImageSelected(flyerTemplate.identifier)
											}}
										>
											<div className={`absolute inset-0 ${props.orientation === 'portrait' ? 'py-8 px-12' : 'py-12 px-8'}`}>
												<div className=" p-5 transition-scale border border-darker-grey bo h-full" dangerouslySetInnerHTML={{ __html: flyerTemplate.template }}></div>
											</div>
										</div>
										<div className="py-6">
											<h5>{flyerTemplate.description}</h5>
										</div>
									</div>
								</SwiperSlide>
							)
						)
					})}

				{props.images && props.images.length > 3 && !isBeginning && !props.orientation && (
					<button className="custom-prev h-[73px] w-[58px] bg-gradient-byf-left absolute left-0 top-1/2 transform -translate-y-1/2 z-10">
						<Image src={forward_arrow_white} onClick={handlePrevSlide} alt="Previous" className="w-8 h-8 rotate-180" />
					</button>
				)}

				{props.images && props.images.length > 3 && !isEnd && !props.orientation && (
					<button className={`custom-next h-[73px] w-[58px] bg-gradient-byf-right absolute right-[3px] top-1/2 transform -translate-y-1/2 z-10`}>
						<Image src={forward_arrow_white} onClick={handleNextSlide} alt="Next" className="w-8 h-8 ml-[27px] " />
					</button>
				)}

				{props.templates && props.templates.length > 3 && !isBeginning && props.orientation && (
					<button className="custom-prev h-full w-[58px] bg-gradient-byf-template-left absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p">
						<Image src={forward_arrow_blue} onClick={handlePrevSlide} alt="Previous" className="w-6 h-8 rotate-180" />
					</button>
				)}

				{props.templates && props.templates.length > 3 && !isEnd && props.orientation && (
					<button className={`custom-next h-full w-[58px] bg-gradient-byf-template-right absolute right-0 top-1/2 transform -translate-y-1/2 z-10`}>
						<Image src={forward_arrow_blue} onClick={handleNextSlide} alt="Next" className="w-6 h-8 ml-[27px] " />
					</button>
				)}
			</Swiper>
		</>
	)
}

export { SwiperWrapperBYF }
